import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IEntityReducer } from '../../../reducers/entities';
import { selectSceneConfig } from './sceneConfig';
import { IScene, ISceneDAO, SceneType } from 'types/domain/scene';
import { Orientation } from 'types/domain/image';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id: number) => id || state.state[EntityTypes.SCENE];
const assets = (state: RootState) => state.core.assets;
const config = selectSceneConfig;

export const selectScene = createCachedSelector([entity, id, assets, config], (entity: IEntityReducer, id: number) => {
  return parseSceneDAO(entity, id);
})((state: RootState, id?: number) => id || state.state[EntityTypes.SCENE] || 'unknown');

export function parseSceneDAO(entity, id) {
  if (!id) return null;

  const scene = entity[EntityTypes.SCENE][id];

  if (!scene) return null;

  const image = scene.imageId ? entity[EntityTypes.IMAGE][scene.imageId] : null;
  const video = scene.videoId ? entity[EntityTypes.VIDEO][scene.videoId] : null;
  const model = scene.modelId ? entity[EntityTypes.MODEL][scene.modelId] : null;
  //const thumbnail = scene.thumbnail ? assets[assetId(EntityTypes.SCENE, id, AssetType.THUMB, null)] : null;
  const orientation =
    scene.type === SceneType.PHOTO
      ? image?.orientation
      : scene.type === SceneType.VIDEO
      ? video?.orientation
      : Orientation.LANDSCAPE;
  return {
    ...scene,
    image,
    video,
    model,
    //thumbnail,
    sceneConfig: scene.sceneConfigId ? entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId] : null,
    portraitVariant: scene.portraitVarId ? entity[EntityTypes.PORTRAIT_VARIANT][scene.portraitVarId] : null,
    orientation,
  } as ISceneDAO;
}

const actionId = (state: RootState, actionId: number) => actionId;

const createdMs = (scene?: IScene) => {
  const timestamp = scene?.created?.date ? new Date(scene.created.date).getTime() : 0;

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

/**
 * The images generated for a slide, newest first. `scene.actionId` is a label the
 * generator writes — it says what the image was made for, not what the slide is using.
 */
export const selectAiScenesForAction = createCachedSelector(
  [entity, actionId],
  (entity: IEntityReducer, actionId: number) => {
    if (!actionId) return [];

    return Object.values(entity[EntityTypes.SCENE])
      .filter((scene: IScene) => scene.actionId === actionId && scene.status > -1)
      .sort((a: IScene, b: IScene) => createdMs(b) - createdMs(a) || b.id - a.id)
      .map((scene: IScene) => parseSceneDAO(entity, scene.id)) as ISceneDAO[];
  },
)((state: RootState, actionId?: number) => actionId || 'unknown');

export const selectScenes = createSelector([entity], (entity: IEntityReducer) => {
  return Object.values(entity[EntityTypes.SCENE])
    .filter(s => s.status > -1)
    .map((scene: IScene) => parseSceneDAO(entity, scene.id));
});
